import { Navigate } from 'react-router-dom'
import { useIdentityQuery } from '@/features/preferences'
import { orgStore } from '@/store/org'

/**
 * Gates any authenticated-but-org-scoped route behind having an active
 * organization membership (plan 024 design decisions 6-7, ADR-0009). Wraps
 * /app and /welcome, inside ProtectedRoute — an unauthenticated visitor never
 * reaches this component at all.
 *
 * Reads GET /api/me via useIdentityQuery(). That route is deliberately NOT
 * @tenant_required (see apps/backend/app/domains/identity/routes.py: me()) so
 * a genuinely org-less caller gets a 200 with orgs: [] instead of a 403 —
 * that 200-with-empty-array is the signal this component acts on.
 *
 * MUST NOT redirect while the query is loading or has errored — either would
 * be the lockout the plan's step-10 STOP condition names (an unresolved
 * query is not evidence the user has no org, only that we don't know yet).
 * Only a resolved, successful response with orgs.length === 0 sends the user
 * to /setup-organization.
 */
export default function RequireOrg({ children }: { children: React.ReactNode }) {
  const { data, isLoading, isError, refetch } = useIdentityQuery()
  const cachedOrg = orgStore((state) => state.org)

  // The identity request is online-only, but an already-authenticated field
  // tab has a session-scoped organisation snapshot. Let the cached shell boot
  // immediately instead of waiting for a request that cannot complete.
  if (!navigator.onLine && cachedOrg) return <>{children}</>

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    )
  }

  if (isError) {
    if (!navigator.onLine && cachedOrg) return <>{children}</>
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background">
        <p className="text-sm text-muted-foreground">
          Couldn't load your workspace. Check your connection and try again.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="h-9 rounded-lg border border-border px-4 text-sm hover:bg-accent transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  if (!data || data.orgs.length === 0) {
    return <Navigate to="/setup-organization" replace />
  }

  return <>{children}</>
}
